/**
 * Detects the user's platform and PWA installation state.
 *
 * iOS Safari and iOS Chrome behave very differently — Apple forces all iOS browsers
 * to use WebKit and only Safari exposes "Add to Home Screen", so we route those flows
 * separately. Everywhere else, we rely on the `beforeinstallprompt` event.
 */

import { useState } from 'react'

export type Platform =
  | 'desktop-installable'
  | 'android-installable'
  | 'ios-safari'
  | 'ios-other'
  | 'desktop-other'
  | 'unknown'

export interface PlatformInfo {
  /** Coarse platform bucket used to choose which install flow to render. */
  platform: Platform
  /** True if the app is currently running as an installed PWA. */
  isStandalone: boolean
  /** True if the device runs iOS or iPadOS. */
  isIOS: boolean
  /** True if the active iOS browser is Safari (the only one that can install PWAs). */
  isIOSSafari: boolean
  /** True for Android user agents — used to pick the bottom-sheet variant. */
  isAndroid: boolean
}

/**
 * Returns a platform fingerprint computed once on mount. UA sniffing isn't perfect,
 * so we keep the matrix coarse and let each install flow add its own guards.
 */
export function usePlatform(): PlatformInfo {
  const [info] = useState<PlatformInfo>(() => detectPlatform())
  return info
}

function detectPlatform(): PlatformInfo {
  if (typeof window === 'undefined') {
    return { platform: 'unknown', isStandalone: false, isIOS: false, isIOSSafari: false, isAndroid: false }
  }

  const ua = window.navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document)
  const isAndroid = /Android/.test(ua)
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true

  // iOS Chrome/Firefox/Edge identify themselves as CriOS/FxiOS/EdgiOS.
  const isIOSSafari = isIOS && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/.test(ua)

  let platform: Platform = 'unknown'
  if (isStandalone) platform = isIOS ? 'ios-safari' : 'desktop-installable'
  else if (isIOS && isIOSSafari) platform = 'ios-safari'
  else if (isIOS) platform = 'ios-other'
  else if (isAndroid) platform = 'android-installable'
  else platform = 'desktop-installable'

  return { platform, isStandalone, isIOS, isIOSSafari, isAndroid }
}
